import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Block } from '@app/core/models/block/block.model';
import { GlobalService, MediaQueryState } from '@app/core/services/common/global.service';
import { BlockActionType } from '@core/enums';
import { BlockAction, ViewState } from '@core/models';

interface PageBtnDef {
  page: any;
  classes: string[];
}

@Component({
  selector: 'app-paginator-view',
  templateUrl: './paginator-view.component.html',
  styleUrls: ['./paginator-view.component.scss'],
  standalone: false,
})
export class PaginatorViewComponent implements OnInit, AfterViewInit {

  @Input() specialBlock: Block;

  constructor(
    private globalService: GlobalService,
    private route: ActivatedRoute,
  ) {
  }

  get mediaQueryState(): MediaQueryState {
    return this.globalService.mediaQuery.value.mediaQueryState;
  }

  ngAfterViewInit(): void {
    if (!this.globalService.selectPagePaginator) {
      this.route.queryParamMap.subscribe(params => {
        this.globalService.selectPagePaginator = +params.get('pageNumber');
        this.parseStructure(this.specialBlock?.children[0]);
      });
    } else {
      this.parseStructure(this.specialBlock?.children[0]);
    }
    this.globalService.mediaQuery
      .subscribe(_ => {
        this.parseStructure(this.specialBlock?.children[0]);
      });
  }

  ngOnInit(): void {
    if (this.specialBlock.settings?.radioButtonPagination?.radioButtonPagination) {
      this.specialBlock.children = this.specialBlock.children.filter(child => child.settings.viewState === ViewState.PAGINATION_PREVIEW_VIEW);
    } else {
      this.specialBlock.children = this.specialBlock.children.filter(child => child.settings.viewState !== ViewState.PAGINATION_PREVIEW_VIEW);
    }
  }

  private addClassesToPages(pages: any[]): PageBtnDef[] {
    return pages.map((num, idx, arr) => {
      const classes = [];
      let page = num;
      if (typeof num === 'string' && num.startsWith('...')) {
        classes.push('dots-page-placeholder');
        page = '...';
      }

      return {
        page,
        classes,
      };
    });
  }

  private getPaginationNumbersForDesktop(lastPage: number, currentPage: number): PageBtnDef[] {
    let result = [];
    if (lastPage <= 11) {
      result = Array.from({length: lastPage}, (_, index) => index + 1);
      return this.addClassesToPages(result);
    }

    if (currentPage <= 8) {
      result = [1, 2, 3, 4, 5, 6, 7, 8, 9, '...lp', lastPage];
      return this.addClassesToPages(result);
    }

    if (currentPage >= 9 && currentPage <= (lastPage - 8)) {
      result = [1, '...fp', (currentPage - 3), (currentPage - 2), (currentPage - 1), currentPage, (currentPage + 1), (currentPage + 2), (currentPage + 3), '...lp', lastPage];
      return this.addClassesToPages(result);
    }

    if (currentPage >= (lastPage - 7)) {
      result = [1, '...fp', (lastPage - 8), (lastPage - 7), (lastPage - 6), (lastPage - 5), (lastPage - 4), (lastPage - 3), (lastPage - 2), (lastPage - 1), lastPage];
      return this.addClassesToPages(result);
    }

    return this.addClassesToPages(result);
  }

  private getPaginationNumbersForMobile(lastPage: number, currentPage: number): PageBtnDef[] {
    let result = [];

    if (lastPage <= 5) {
      result = Array.from({length: lastPage}, (_, index) => index + 1);
      return this.addClassesToPages(result);
    }

    if (currentPage <= 2) {
      result = [1, 2, 3, '...lp', lastPage];
      return this.addClassesToPages(result);
    }

    if (currentPage >= 3 && currentPage <= (lastPage - 2)) {
      result = [1, (currentPage - 1), currentPage, (currentPage + 1), lastPage];
      return this.addClassesToPages(result);
    }

    if (currentPage === lastPage - 1) {
      result = [1, '...fp', (currentPage - 1), currentPage, lastPage];
      return this.addClassesToPages(result);
    }

    if (currentPage === lastPage) {
      result = [1, '...fp', (lastPage - 2), (lastPage - 1), lastPage];
      return this.addClassesToPages(result);
    }

    return this.addClassesToPages(result);
  }

  private getPaginationNumbersForTablet(lastPage: number, currentPage: number): PageBtnDef[] {
    let result = [];

    if (lastPage <= 7) {
      result = Array.from({length: lastPage}, (_, index) => index + 1);
      return this.addClassesToPages(result);
    }

    if (currentPage <= 4) {
      result = [1, 2, 3, 4, 5, '...lp', lastPage];
      return this.addClassesToPages(result);
    }

    if (currentPage >= 5 && currentPage <= (lastPage - 4)) {
      result = [1, '...fp', (currentPage - 1), currentPage, (currentPage + 1), '...lp', lastPage];
      return this.addClassesToPages(result);
    }

    if (currentPage >= (lastPage - 3)) {
      result = [1, '...fp', (lastPage - 4), (lastPage - 3), (lastPage - 2), (lastPage - 1), lastPage];
      return this.addClassesToPages(result);
    }

    return this.addClassesToPages(result);
  }

  private getSinglePageButton(template: Block, page: PageBtnDef, currentPage: number): Block {
    const clone = template.clone();
    clone.additionalClasses = page.classes;
    clone.children = [this.getSinglePageButtonChild(clone, page, currentPage)];
    return clone;
  }

  private getSinglePageButtonChild(pageButton: Block, page: PageBtnDef, currentPage: number): Block {
    const child = pageButton.children[0].clone();
    child.content = page.page;
    child.action = null;
    child.additionalClasses = [];
    if (currentPage === page.page) {
      child.additionalClasses.push(`${child.uId}__active`);
    } else {
      const action = new BlockAction(BlockActionType.GET);
      action.actionData = {type: BlockActionType.GET};
      action.actionName = page.page.toString();
      child.action = action;
    }
    return child;
  }

  private getArrowLeft(block: Block): Block {
    const arrowLeft = block.children[0].clone();
    arrowLeft.additionalClasses.push('arrow-left-btn');
    return arrowLeft;
  }

  private getArrowRight(block: Block): Block {
    const arrowRight = block.children.at(-1).clone();
    arrowRight.additionalClasses.push('arrow-right-btn');
    return arrowRight;
  }

  private parseStructure(block: Block): void {
    if (block.children.length === 1 && block.children[0] !== block) {
      this.parseStructure(block.children[0]);
      return;
    }
    if (block.children.length > 3) {
      const currentPage = this.globalService.selectPagePaginator || 1;
      const newChildren = [];
      if (currentPage > 1) {
        newChildren.push(this.getArrowLeft(block));
      }
      const childTemplate = block.children[1];
      const lastPage = this.specialBlock.settings.allPages;
      let pageNumbers: PageBtnDef[];

      switch (this.mediaQueryState) {
        case MediaQueryState.Desktop:
          pageNumbers = this.getPaginationNumbersForDesktop(lastPage, currentPage);
          break;
        case MediaQueryState.Tablet:
          pageNumbers = this.getPaginationNumbersForTablet(lastPage, currentPage);
          break;
        case MediaQueryState.Mobile:
          pageNumbers = this.getPaginationNumbersForMobile(lastPage, currentPage);
          break;
        default:
          pageNumbers = [];
      }

      for (const page of pageNumbers) {
        newChildren.push(
          this.getSinglePageButton(childTemplate, page, currentPage),
        );
      }
      if (currentPage < lastPage) {
        newChildren.push(this.getArrowRight(block));
      }
      block.children = newChildren;
    }
  }
}
